import { StatusDot, Tag, type Column } from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { LICENCE_LABEL, day, daysUntil, licenceState, type BrandRow } from './types.ts';

/**
 * The brand table.
 *
 * Every cell prints something the API actually sent. The one derived value is
 * the licence STATE, and it is derived from the server's own `licence_expiring`
 * and `licence_expired` flags rather than from a second calculation here — the
 * dashboard warning, the settings screen and this table have to agree about
 * what "inside the window" and "lapsed" mean, and a browser-side copy of either
 * is a chance to disagree. See `licenceState` in `./types.ts` for the twelve
 * hours the EXPIRED half used to be wrong for.
 *
 * The day COUNT below is a different thing from the STATE, and it is the only
 * value on this screen still read off the reader's own clock. It is a countdown
 * for the eye; it decides nothing.
 */
export const brandColumns: Column<BrandRow>[] = [
  {
    key: 'brand',
    header: 'Brand',
    render: (b) => (
      <>
        {b.name}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          <span className="tabular">{b.ref}</span>
        </div>
      </>
    ),
  },
  {
    key: 'domain',
    header: 'Host',
    render: (b) => (
      <>
        <span className="tabular">{b.domain}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {b.active
            ? 'resolves to this brand'
            : 'suspended — this host serves nobody'}
        </div>
      </>
    ),
  },
  {
    key: 'players',
    header: 'Players',
    numeric: true,
    secondary: true,
    render: (b) => <span className="tabular">{formatCount(Number(b.players))}</span>,
  },
  {
    key: 'licence',
    header: 'Licence',
    render: (b) => b.licence_ref ?? <span className="text-subtle">none recorded</span>,
  },
  {
    key: 'expires',
    header: 'Licence expires',
    render: (b) => {
      const state = licenceState(b);
      if (state === 'NONE') {
        return <span className="text-subtle">no expiry recorded</span>;
      }
      const remaining = daysUntil(b.licence_expires_at!);
      return (
        <>
          <StatusDot
            /* Three tones, not four: StatusDot ships ok · attention · quiet,
               and a lapsed licence takes `attention` beside the Lapsed tag
               rather than a colour the design system does not define. */
            tone={state === 'IN_DATE' ? 'ok' : 'attention'}
            label={LICENCE_LABEL[state]}
          />{' '}
          <span className="tabular">{day(b.licence_expires_at)}</span>
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {/* "0 days" is the reading the rounding produces on the day itself,
                and it is now REACHABLE: EXPIRED comes from the server, so a
                licence that ran out a few hours ago is lapsed here while the
                countdown still rounds to nought. "lapsed 0 days ago" reads as a
                bug; "lapsed today" is what it means. */}
            {state === 'EXPIRED'
              ? remaining === 0
                ? 'lapsed today'
                : `lapsed ${formatCount(Math.abs(remaining))} days ago`
              : state === 'EXPIRING'
                ? remaining === 0
                  ? 'renew — expires today'
                  : `renew — ${formatCount(remaining)} days left, inside the 60-day window`
                : `in ${formatCount(remaining)} days`}
          </div>
        </>
      );
    },
  },
  {
    key: 'active',
    header: 'Status',
    render: (b) => (
      <Tag tone={b.active ? 'accent' : 'quiet'}>{b.active ? 'Live' : 'Suspended'}</Tag>
    ),
  },
];
